import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import colors from "styles/colors";
import TitleLabel from "components/TitleLabel";
import AccountBox from "components/AccountBox";
import FacebookPageList from "components/facebook/FacebookPageList";
import FacebookFormList from "components/facebook/FacebookFormList";
import facebookActions from "modules/facebook/actions";
import facebookSelectors from "modules/facebook/selectors";
import FacebookSigninButton from "components/facebook/FacebookSigninButton";

const FacebookSettingContent = ({ leadFormId, currentTab, action }) => {
  const rmpToken = sessionStorage.getItem("rmp_token");
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const facebookPageSettingResult = useSelector((state) => facebookSelectors.getFacebookPageSettingResult(state));
  const facebookUserInfoResult = useSelector((state) => facebookSelectors.getFacebookUserInfoResult(state));
  const facebookPageList = useSelector((state) => facebookSelectors.getFacebookPageListResult(state));
  const fbConnectedResult = useSelector((state) => facebookSelectors.setFbConnectedResult(state));
  const fbDisconnectedResult = useSelector((state) => facebookSelectors.setFbDisconnectedResult(state));
  const validateFacebookLeadFormResult = useSelector((state) =>
    facebookSelectors.setValidateFacebookLeadFormResult(state),
  );
  const facebookFormListResult = useSelector((state) => facebookSelectors.getFacebookFormListResult(state));
  const facebookFormLoading = useSelector((state) => facebookSelectors.setFacebookFormLoadingResult(state));
  const facebookLoading = useSelector((state) => facebookSelectors.setFacebookLoadingResult(state));
  const facebookError = useSelector((state) => facebookSelectors.setFacebookErrorResult(state));
  const [facebookData, setFacebookData] = useState({});

  useEffect(() => {
    dispatch(
      facebookActions.getFacebookPageSetting({
        token: rmpToken,
        id: leadFormId,
      }),
    );
  }, []);

  useEffect(() => {
    const createData = (accountName, accountImageLink, pageList, pageName, pageId, pageImg, disabled) => ({
      accountName,
      accountImageLink,
      pageList,
      pageName,
      pageId,
      pageImg,
      disabled,
    });

    /**
     * don't have connection setting in database
     */
    const handleFacebookPageListResult = () => {
      if (
        facebookPageList.result !== 0 ||
        (facebookPageList.result == 0 && facebookPageList.data.pageList == null) ||
        (facebookPageList.result == 0 && facebookPageList.data.pageList.length == 0)
      )
        return;
      if (facebookError.data.error) return;
      const data = createData(
        facebookUserInfoResult.data.name,
        facebookUserInfoResult.data.image_link,
        facebookPageList.data.pageList,
        facebookPageList.data.pageList[0].name,
        facebookPageList.data.pageList[0].id,
        facebookPageList.data.pageList[0].picture,
        false,
      );
      setFacebookData(data);
    };

    /**
     * have connection setting in database
     */
    const handleFacebookPageSettingResult = () => {
      if (facebookPageSettingResult.result !== 0) return;
      if (facebookPageSettingResult.result === 0 && facebookPageSettingResult.msg === "NO_DATA") return;
      if (!fbConnectedResult.data.fbConnected) return;
      if (facebookError.data.error) return;
      const data = createData(
        facebookPageSettingResult.data.fb_account_name,
        facebookPageSettingResult.data.fb_image_link,
        null,
        facebookPageSettingResult.data.fb_page_name,
        facebookPageSettingResult.data.fb_page_id,
        facebookPageSettingResult.data.fb_page_image_link,
        true,
      );
      setFacebookData(data);
    };

    setFacebookData({});

    if (facebookUserInfoResult.result === 0 && facebookUserInfoResult.msg === "GET_DATA_COMPLETED") {
      if (
        facebookPageSettingResult.result === 0 &&
        facebookPageSettingResult.msg === "GET_DATA_COMPLETED" &&
        !fbDisconnectedResult.data.fbDisconnected
      ) {
        if (facebookPageSettingResult.data.facebook_message_error === "token_expire") {
          handleFacebookPageSettingResult();
        } else {
          handleFacebookPageListResult();
        }
      } else {
        handleFacebookPageListResult();
      }
    } else {
      if (!fbDisconnectedResult.data.fbDisconnected) {
        handleFacebookPageSettingResult();
      }
    }
  }, [
    facebookPageSettingResult,
    facebookUserInfoResult,
    facebookPageList,
    fbConnectedResult,
    fbDisconnectedResult,
    facebookError,
  ]);

  return Object.keys(facebookData).length !== 0 && !facebookLoading.data.loading ? (
    <>
      <TitleLabel title={t("__fbleads_fb_account")} required={false} />
      <AccountBox
        imageLink={facebookData.accountImageLink}
        name={facebookData.accountName}
        accountType="facebook"
        leadType="facebook"
      />
      <TitleLabel title={t("__fbleads_fb_page")} required={true} />
      <FacebookPageList
        fbPageList={facebookData.pageList}
        fbPageName={facebookData.pageName}
        fbPageId={facebookData.pageId}
        fbPageImg={facebookData.pageImg}
        fbValueDisabled={facebookData.disabled}
        currentTab={currentTab}
      />
      <FormLabel>
        <TitleLabel title={t("__fbleads_fb_form")} required={true} />
      </FormLabel>
      <FacebookFormList
        errorStatus={validateFacebookLeadFormResult.data.validateForm.result}
        action={action}
        leadFormId={leadFormId}
      />
      {validateFacebookLeadFormResult.data.validateForm.result &&
      validateFacebookLeadFormResult.data.validateForm.msg != "selected_form_have_used" ? (
        <TextWarning>{t("__select_form_warning")}</TextWarning>
      ) : (
        validateFacebookLeadFormResult.data.validateForm.result &&
        validateFacebookLeadFormResult.data.validateForm.msg == "selected_form_have_used" && (
          <TextWarning>{t("__selected_form_warning")}</TextWarning>
        )
      )}
    </>
  ) : facebookFormListResult.result == 1 &&
    !facebookFormLoading.data.loading &&
    !facebookLoading.data.loading &&
    facebookError.data.error ? (
    <FacebookSigninButton currentTab={currentTab} />
  ) : facebookLoading.data.loading ? (
    <LoadingArea>
      <StyledLoadingIcon />
    </LoadingArea>
  ) : (
    <FacebookSigninButton currentTab={currentTab} />
  );
};
export default FacebookSettingContent;

const FormLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const TextWarning = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  color: ${colors.negative};
  font-size: 12px;
  font-weight: normal;
  position: relative;
  line-height: 1.5rem;
`;

const LoadingArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  width: 100%;
  height: 100%;
`;

const StyledLoadingIcon = styled.div`
  border: 5px solid #ccc;
  border-top: 5px solid #eee;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: button-keyframe-spin 1s linear infinite;
  box-sizing: initial !important;
  position: sticky;

  @keyframes button-keyframe-spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
